import React, { useState, useRef, useEffect } from "react";
import { ReactComponent as DropdownArrow } from "../../../assets/icons/dropdownArrow.svg";
import { toast } from "react-toastify";

interface DropdownProps {
  label: string;
  options: string[];
  productType?: string; // Optional, used for limiting selections
  onSelect: (selected: string) => void; // Will pass comma-separated string
  reset?: boolean;
  setReset?: React.Dispatch<React.SetStateAction<boolean>>;
}

const MultiSelectDropdown: React.FC<DropdownProps> = ({
  label,
  options,
  onSelect,
  reset,
  setReset,
  productType,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    setDropdownOpen(prev => !prev);
  };
  const handleSelect = (option: string) => {
    const trends = sessionStorage.getItem("trends");

    if (option === "Incorrect Trend analysis" && !trends) {
      toast.error("Run Trend Analysis to enable this option.", {
        hideProgressBar: true,
      });
      return;
    }

    setSelectedOptions(prev => {
      if (!productType) {
        toast.error("Please select product type to activate this option", {
          hideProgressBar: true,
        });
        return prev;
      }
      let updated;
      if (prev.includes(option)) {
        updated = prev.filter(item => item !== option);
      } else {
        const limit = 1;
        if (prev.length >= limit) {
          toast.error(
            `You can select only ${limit} option${limit > 1 ? "s" : ""}.`,
            { hideProgressBar: true }
          );
          return prev;
        }
        updated = [...prev, option];
      }
      onSelect(updated.join(", ")); // Send comma-separated string
      return updated;
    });
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    setSelectedOptions([]);
    onSelect(""); // Clear the selection callback too
  }, [productType]);
  useEffect(() => {
    if (reset && setReset) {
      setSelectedOptions([]);
      setReset(false);
    }
  }, [reset]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="mt-0 block w-full pl-3 pr-10 py-1 text-left text-base border border-default rounded-lg"
        onClick={toggleDropdown}
      >
        <span className="text-secondary text-xs">
          {selectedOptions.length > 0 ? selectedOptions.join(", ") : label}
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <span
            className={`transition-transform duration-300 ${
              dropdownOpen ? "rotate-180" : ""
            }`}
          >
            <DropdownArrow className="w-2 h-2" />
          </span>
        </span>
      </button>

      {dropdownOpen && (
        <div className="dropdown-menu-scroll absolute mt-0 z-10 max-h-48 w-full overflow-y-auto rounded-md bg-white shadow-lg">
          {options.map((option, index) => {
            const isSelected = selectedOptions.includes(option);
            return (
              <button
                key={index}
                type="button"
                className={`block w-full text-left px-4 py-2 text-xs border-b-2 border-light ${
                  isSelected ? "bg-gray-200 font-semibold" : "text-secondary"
                }`}
                onClick={() => handleSelect(option)}
              >
                {option}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
